/**
 * WeQuote CRM — Company Detail / Engagement View (Phase 4)
 * Tasks 4.1–4.9: Company info panel, communication links, recent activity, quick stats
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiGet, apiPost, apiDelete, apiPut } from '../../utils/api'

function fmtSecs(s?: number): string {
  const n = Number(s) || 0
  if (n <= 0) return '0m'
  if (n < 3600) return `${Math.floor(n / 60)}m`
  const h = Math.floor(n / 3600); const m = Math.floor((n % 3600) / 60)
  return m ? `${h}h ${m}m` : `${h}h`
}

// Resolution tag stored in the chat's tags JSON (set when an agent closes a chat).
function chatResolution(c: any): string | null {
  try { const t = typeof c.tags === 'string' ? JSON.parse(c.tags) : (c.tags || []); return Array.isArray(t) && t.length ? String(t[0]) : null }
  catch { return null }
}
// Resolved only when closed AND tagged "Resolved"; everything else is unresolved.
function isChatResolved(c: any): boolean {
  return c.status === 'closed' && (chatResolution(c) || '').toLowerCase() === 'resolved'
}

// ─── Types ─────────────────────────────────────────────────────────────────
interface PaidCustomer {
  id: number; wequote_id?: number; alias?: string
  company_name: string; company_logo_url?: string
  address_line_1?: string; address_line_2?: string; address_line_3?: string
  city?: string; county?: string; country?: string; postcode?: string
  contact_name?: string; email?: string; phone?: string
  account_type?: string; user_count?: number; stock_module_version?: string
  status: string; last_activity_at?: string; widget_visit_count: number
  extra_data?: string
}

interface CompanyUser {
  name: string; email: string; phone: string
}

interface ChannelLink {
  id: number; paid_customer_id: number; channel_type: string
  channel_ref_id: string; channel_name: string
  link_status: string; match_confidence: number; match_method: string
}

interface RecentMessage {
  id: number; channel: string; sender_name: string; message_text: string
  created_at: string
}

interface EngagementScoreResponse {
  engagement_score: number
}

interface ActivityData {
  totalMessages: number
  widget_visit_count: number
  wa_archive?: { messageCount: number }
  wa_live?: { messageCount: number }
  zoho?: { messageCount: number }
  widget?: { messageCount: number }
}

// ─── Channel Icon ──────────────────────────────────────────────────────────
const ChannelIcon: React.FC<{ type: string }> = ({ type }) => {
  if (type === 'wa_archive' || type === 'wa_live') {
    return (
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${type === 'wa_live' ? 'bg-green-100' : 'bg-green-50'}`}>
        <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /><path d="M12 2C6.478 2 2 6.478 2 12c0 1.768.462 3.426 1.274 4.866L2 22l5.234-1.258A9.949 9.949 0 0012 22c5.522 0 10-4.478 10-10S17.522 2 12 2zm0 18.2A8.167 8.167 0 017.7 18.6l-.3-.18-3.106.814.828-3.024-.197-.312A8.167 8.167 0 013.8 12c0-4.528 3.672-8.2 8.2-8.2s8.2 3.672 8.2 8.2-3.672 8.2-8.2 8.2z" /></svg>
      </div>
    )
  }
  if (type === 'zoho') {
    return (
      <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
        <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
      </div>
    )
  }
  // widget
  return (
    <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
      <svg className="w-4 h-4 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" /></svg>
    </div>
  )
}

const channelLabel = (type: string): string => {
  const labels: Record<string, string> = {
    wa_archive: 'WhatsApp Group', wa_live: 'WhatsApp Group',
    zoho: 'Zoho Chat', widget: 'Live Website Support Widget Chat',
  }
  return labels[type] || type
}

const formatMessageTime = (dateStr: string): string => {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const truncateMessage = (text: string, maxLength: number = 60): string => {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

// Simple sparkline component showing channel activity breakdown
const ActivitySparkline: React.FC<{ activity: ActivityData | null }> = ({ activity }) => {
  if (!activity) return null

  const channels = [
    { name: 'WA Archive', count: activity.wa_archive?.messageCount || 0, color: 'bg-green-500' },
    { name: 'WA Live', count: activity.wa_live?.messageCount || 0, color: 'bg-emerald-500' },
    { name: 'Zoho', count: activity.zoho?.messageCount || 0, color: 'bg-orange-500' },
    { name: 'Widget', count: activity.widget?.messageCount || 0, color: 'bg-blue-500' },
  ]

  const maxCount = Math.max(...channels.map(ch => ch.count), 1)

  return (
    <div className="mt-4 pt-4 border-t border-border">
      <p className="text-xs font-semibold text-txt-secondary uppercase tracking-wider mb-3">Activity Distribution</p>
      <div className="space-y-2">
        {channels.map(ch => (
          <div key={ch.name} className="flex items-center gap-2">
            <span className="text-[10px] text-txt-secondary w-16 truncate">{ch.name}</span>
            <div className="flex-1 h-1.5 bg-surface-secondary rounded-full overflow-hidden">
              <div
                className={`h-full ${ch.color} transition-all duration-300`}
                style={{ width: `${maxCount > 0 ? (ch.count / maxCount) * 100 : 0}%` }}
              />
            </div>
            <span className="text-[10px] text-txt-secondary w-8 text-right">{ch.count}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Accordion Section ─────────────────────────────────────────────────────
// Collapsible panel used for the right-hand sections. Header is a real <button>
// (keyboard-toggle + focus for free), exposes aria-expanded, and shows an
// optional leading icon, a count badge and a rotating chevron.
const AccordionSection: React.FC<{
  title: string
  icon?: React.ReactNode
  badge?: React.ReactNode
  open: boolean
  onToggle: () => void
  bodyClassName?: string
  children: React.ReactNode
}> = ({ title, icon, badge, open, onToggle, bodyClassName, children }) => {
  const panelId = useMemo(() => `acc-${title.replace(/\s+/g, '-').toLowerCase()}-${Math.random().toString(36).slice(2, 8)}`, [title])
  return (
    <div className="bg-surface rounded-xl border border-border overflow-hidden shrink-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-center justify-between gap-3 px-6 py-4 hover:bg-surface-hover transition-colors duration-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset"
      >
        <h3 className="text-sm font-semibold text-txt-primary flex items-center gap-2 min-w-0">
          {icon}
          <span className="truncate">{title}</span>
          {badge}
        </h3>
        <svg
          className={`w-4 h-4 text-txt-secondary flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
      {open && (
        <div id={panelId} className={bodyClassName ?? 'border-t border-border'}>
          {children}
        </div>
      )}
    </div>
  )
}

// ─── Zoho Chat Preview Modal ──────────────────────────────────────────────
interface ZohoChatDetail {
  id: number; chat_number: number; visitor_name: string | null; visitor_email: string | null
  chat_date: string | null; department: string | null; operator: string | null; message_count: number
  raw_text?: string; status: string
}

interface ParsedMsg { type: 'message' | 'system'; sender: string; text: string; timestamp: string }

const BOT_NAMES_LIST = ['wequote', 'wequote whatsapp']
const SYSTEM_PHRASES_LIST = ['forwarded the chat request', 'accepted the chat transfer', 'has ended this chat',
  'has reopened this chat', 'has left this chat', 'ended this chat session', 'transferred the chat',
  'joined this chat', 'chat session timed out', 'idle chat timeout']

function parseTranscript(raw: string, visitor?: string | null, operator?: string | null): ParsedMsg[] {
  const text = raw.replace(/\t/g, ' ').replace(/ {2,}/g, ' ')
  const lines = text.split('\n')
  const msgs: ParsedMsg[] = []
  let startIdx = 0
  for (let i = 0; i < lines.length; i++) { if (lines[i].trim().toLowerCase().startsWith('chat duration')) { startIdx = i + 1; break } }
  const known = new Set<string>()
  if (visitor) known.add(visitor.trim().toLowerCase())
  if (operator) known.add(operator.trim().toLowerCase())
  BOT_NAMES_LIST.forEach(b => known.add(b))
  const TIME_RE = /^\d{1,2}:\d{2}(:\d{2})?\s*(AM|PM)$/i
  const DATE_TIME_RE = /^\d{1,2}\s+\w{3},?\s+\d{1,2}:\d{2}(:\d{2})?\s*(AM|PM)?$/i
  let curSender = '', curLines: string[] = [], curTs = ''
  const flush = () => {
    const t = curLines.join(' ').trim()
    if (!t && !curSender) return
    const isSys = SYSTEM_PHRASES_LIST.some(p => t.toLowerCase().includes(p))
    if (isSys) msgs.push({ type: 'system', sender: '', text: t, timestamp: curTs })
    else if (curSender && t) msgs.push({ type: 'message', sender: curSender, text: t, timestamp: curTs })
    curLines = []; curTs = ''
  }
  for (let i = startIdx; i < lines.length; i++) {
    const tr = lines[i].trim()
    if (!tr) continue
    if (TIME_RE.test(tr) || DATE_TIME_RE.test(tr)) { curTs = tr; flush(); curSender = ''; continue }
    if (SYSTEM_PHRASES_LIST.some(p => tr.toLowerCase().includes(p))) { flush(); curSender = ''; curLines = [tr]; continue }
    if (curLines.length === 0 && known.has(tr.toLowerCase())) { curSender = tr; continue }
    if (curLines.length === 0 && tr.length < 40 && !tr.includes('.') && !tr.includes('?')) {
      const words = tr.split(/\s+/)
      if (words.length <= 3 && words.every(w => /^[A-Z]/.test(w))) { curSender = tr; continue }
    }
    curLines.push(tr)
  }
  flush()
  return msgs
}

const ZohoChatModal: React.FC<{ chatId: number; onClose: () => void }> = ({ chatId, onClose }) => {
  const [detail, setDetail] = useState<ZohoChatDetail | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    apiGet<ZohoChatDetail>(`/zoho-chats/${chatId}`)
      .then(d => { setDetail(d); setLoading(false) })
      .catch(() => setLoading(false))
  }, [chatId])

  const messages = useMemo(() => {
    if (!detail?.raw_text) return []
    return parseTranscript(detail.raw_text, detail.visitor_name, detail.operator)
  }, [detail])

  const classifySender = (s: string): 'bot' | 'visitor' | 'operator' => {
    const l = s.toLowerCase().trim()
    if (BOT_NAMES_LIST.some(b => l.startsWith(b))) return 'bot'
    if (detail?.operator && l === detail.operator.toLowerCase().trim()) return 'operator'
    return 'visitor'
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-surface rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-txt-primary">
                {loading ? 'Loading...' : `Chat #${detail?.chat_number || chatId}`}
              </h3>
              {detail && (
                <p className="text-xs text-txt-secondary">
                  {detail.visitor_name || 'Unknown'} &middot; {detail.chat_date ? new Date(detail.chat_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''} &middot; {detail.message_count} messages
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-txt-secondary hover:text-txt-primary p-1 rounded-md hover:bg-surface-hover">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Chat transcript */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-surface-secondary/30">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <svg className="animate-spin w-6 h-6 text-brand-600" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            </div>
          ) : messages.length === 0 ? (
            <p className="text-sm text-txt-secondary text-center py-8">No transcript available</p>
          ) : (
            <>
              <div className="flex justify-center">
                <span className="text-[10px] text-txt-secondary bg-surface border border-border px-3 py-1 rounded-full">
                  Chat #{detail?.chat_number} &middot; {detail?.chat_date ? new Date(detail.chat_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
              </div>
              {messages.map((msg, i) => {
                if (msg.type === 'system') return (
                  <div key={i} className="flex justify-center">
                    <span className="text-[11px] text-txt-secondary italic bg-surface border border-border/40 px-3 py-1 rounded-full">{msg.text}</span>
                  </div>
                )
                const role = classifySender(msg.sender)
                const isVisitor = role === 'visitor'
                return (
                  <div key={i} className={`flex ${isVisitor ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] ${isVisitor ? 'items-end' : 'items-start'}`}>
                      <p className={`text-[10px] font-semibold mb-0.5 ${isVisitor ? 'text-right text-blue-600' : role === 'bot' ? 'text-slate-500' : 'text-emerald-600'}`}>
                        {msg.sender} {role === 'bot' && <span className="text-[9px] bg-slate-200 text-slate-500 px-1 rounded ml-1">BOT</span>}
                      </p>
                      <div className={`inline-block rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed ${
                        isVisitor ? 'bg-blue-500 text-white rounded-tr-md' : role === 'bot' ? 'bg-slate-200/80 text-txt-primary rounded-tl-md' : 'bg-surface text-txt-primary border border-border/60 shadow-sm rounded-tl-md'
                      }`}>{msg.text}</div>
                      {msg.timestamp && <p className="text-[10px] text-txt-secondary/50 mt-0.5">{msg.timestamp}</p>}
                    </div>
                  </div>
                )
              })}
              <div className="flex justify-center pt-2">
                <span className="text-[11px] text-emerald-600 bg-emerald-50 border border-emerald-200 px-4 py-1 rounded-full font-medium flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>
                  CHAT COMPLETED
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Link Search Modal ─────────────────────────────────────────────────────
const LinkSearchModal: React.FC<{
  channelType: string; customerId: number; onClose: () => void; onLinked: () => void
}> = ({ channelType, customerId, onClose, onLinked }) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<any[]>([])
  const [searching, setSearching] = useState(false)

  const doSearch = async () => {
    setSearching(true)
    try {
      const data = await apiGet<any[]>(`/paid-customers/search-channels/${channelType}`, { q: query })
      setResults(data || [])
    } catch { setResults([]) }
    finally { setSearching(false) }
  }

  useEffect(() => { doSearch() }, []) // initial load

  const handleLink = async (refId: string, name: string) => {
    try {
      await apiPost(`/paid-customers/${customerId}/link`, {
        channel_type: channelType, channel_ref_id: refId, channel_name: name,
      })
      onLinked()
      onClose()
    } catch (err: any) { alert(err.message) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-surface rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-txt-primary">Link {channelLabel(channelType)}</h3>
          <button onClick={onClose} className="text-txt-secondary hover:text-txt-primary text-xl">&times;</button>
        </div>
        <div className="px-6 py-3 border-b border-border">
          <div className="flex gap-2">
            <input type="text" value={query} onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doSearch()}
              placeholder="Search by name or email..." className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-surface" />
            <button onClick={doSearch} className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700">Search</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {searching ? (
            <p className="text-sm text-txt-secondary text-center py-4">Searching...</p>
          ) : results.length === 0 ? (
            <p className="text-sm text-txt-secondary text-center py-4">No results found</p>
          ) : results.map((r, i) => {
            const name = r.group_name || r.display_name || r.chat_name || r.visitor_name || r.wequote_account_id || `#${r.id}`
            const refId = String(r.id || r.chat_id)
            const detail = r.message_count ? `${r.message_count} messages` : r.participant_count ? `${r.participant_count} members` : r.visitor_email || r.plan_tier || ''
            return (
              <div key={i} className="flex items-center justify-between px-3 py-2 border border-border rounded-lg hover:bg-surface-hover cursor-pointer"
                onClick={() => handleLink(refId, name)}>
                <div>
                  <p className="text-sm font-medium text-txt-primary">{name}</p>
                  {detail && <p className="text-xs text-txt-secondary">{detail}</p>}
                </div>
                <button className="px-3 py-1 text-xs font-medium text-brand-600 border border-brand-200 rounded-md hover:bg-brand-50">Link</button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── Edit Details Modal ────────────────────────────────────────────────────
const EditModal: React.FC<{ customer: PaidCustomer; onClose: () => void; onSaved: () => void }> = ({ customer, onClose, onSaved }) => {
  const [form, setForm] = useState<Record<string, any>>({ ...customer })
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      await apiPut(`/paid-customers/${customer.id}`, form)
      onSaved()
      onClose()
    } catch (err: any) { alert(err.message) }
    finally { setSaving(false) }
  }

  const Field = ({ label, field, type = 'text' }: { label: string; field: string; type?: string }) => (
    <div>
      <label className="block text-xs font-medium text-txt-secondary mb-1">{label}</label>
      <input type={type} value={form[field] || ''} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
        className="w-full px-3 py-1.5 text-sm border border-border rounded-lg bg-surface text-txt-primary" />
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-surface rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-surface">
          <h3 className="text-lg font-semibold text-txt-primary">Edit Details</h3>
          <button onClick={onClose} className="text-txt-secondary hover:text-txt-primary text-xl">&times;</button>
        </div>
        <div className="p-6 space-y-3">
          <Field label="Company Name" field="company_name" />
          <div className="grid grid-cols-2 gap-3"><Field label="WeQuote ID" field="wequote_id" type="number" /><Field label="Alias" field="alias" /></div>
          <div className="grid grid-cols-2 gap-3"><Field label="Email" field="email" /><Field label="Phone" field="phone" /></div>
          <Field label="Contact Name" field="contact_name" />
          <Field label="Address" field="address_line_1" />
          <div className="grid grid-cols-2 gap-3"><Field label="City" field="city" /><Field label="County" field="county" /></div>
          <div className="grid grid-cols-2 gap-3"><Field label="Country" field="country" /><Field label="Postcode" field="postcode" /></div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Account Type" field="account_type" />
            <Field label="Users" field="user_count" type="number" />
            <Field label="Stock Module" field="stock_module_version" />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 text-sm text-txt-secondary hover:bg-surface-hover rounded-lg">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Component ────────────────────────────────────────────────────────
export const CompanyDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [customer, setCustomer] = useState<PaidCustomer | null>(null)
  const [links, setLinks] = useState<ChannelLink[]>([])
  const [activity, setActivity] = useState<ActivityData | null>(null)
  const [recentMessages, setRecentMessages] = useState<RecentMessage[]>([])
  const [engagementScore, setEngagementScore] = useState<number | null>(null)
  const [activeChannelsCount, setActiveChannelsCount] = useState(0)
  const [companyChats, setCompanyChats] = useState<any[]>([])
  const [chatPeople, setChatPeople] = useState<any[]>([])
  const [chatTotalSecs, setChatTotalSecs] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showEdit, setShowEdit] = useState(false)
  const [linkingChannel, setLinkingChannel] = useState<string | null>(null)
  const [zohoExpanded, setZohoExpanded] = useState(false)
  const [zohoModalChatId, setZohoModalChatId] = useState<number | null>(null)
  const [zohoSearch, setZohoSearch] = useState('')
  // Contacts (from the Contacts database) associated to this company (Lee, Oct 2026).
  const [companyContacts, setCompanyContacts] = useState<any[]>([])

  // ── Accordion open/closed state (persisted per-section in localStorage) ──
  // Sensible defaults: the two most useful panels (communication links &
  // recent activity) start open; the rest collapsed.
  type SectionKey = 'channelSummary' | 'links' | 'recent' | 'chats' | 'users' | 'contacts'
  const ACCORDION_STORAGE_KEY = 'wq_company_detail_accordions'
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(() => {
    const defaults: Record<SectionKey, boolean> = {
      channelSummary: false, links: true, recent: true, chats: false, users: false, contacts: false,
    }
    try {
      const stored = localStorage.getItem(ACCORDION_STORAGE_KEY)
      if (stored) return { ...defaults, ...JSON.parse(stored) }
    } catch { /* ignore */ }
    return defaults
  })
  const toggleSection = useCallback((key: SectionKey) => {
    setOpenSections(prev => {
      const next = { ...prev, [key]: !prev[key] }
      try { localStorage.setItem(ACCORDION_STORAGE_KEY, JSON.stringify(next)) } catch { /* ignore */ }
      return next
    })
  }, [])

  const fetchData = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      const [custData, actData, msgData, scoreData, chatsData] = await Promise.all([
        apiGet<any>(`/paid-customers/${id}`),
        apiGet<any>(`/paid-customers/${id}/activity`).catch(() => null),
        apiGet<RecentMessage[]>(`/paid-customers/${id}/recent-messages`, { limit: 10 }).catch(() => []),
        apiGet<EngagementScoreResponse>(`/paid-customers/${id}/engagement-score`).catch(() => null),
        apiGet<any>(`/paid-customers/${id}/chats`).catch(() => null),
      ])
      setCustomer(custData.customer)
      setLinks(custData.links || [])
      setCompanyChats(chatsData?.chats || [])
      setChatPeople(chatsData?.people || [])
      setChatTotalSecs(Number(chatsData?.total_support_seconds) || 0)
      setActivity(actData)
      const msgs = Array.isArray(msgData) ? msgData : (msgData as any)?.messages || []
      setRecentMessages(msgs)
      setEngagementScore(scoreData?.engagement_score ?? null)

      // Count active channels
      if (actData) {
        const activeCount = [actData.wa_archive, actData.wa_live, actData.zoho, actData.widget]
          .filter(ch => ch && ch.messageCount > 0).length
        setActiveChannelsCount(activeCount)
      }
    } catch (err) {
      console.error('Failed to load customer:', err)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { fetchData() }, [fetchData])

  // Pull Contacts (from the Contacts DB) whose company matches this customer, so
  // they live alongside the customer. Best-effort — a failure just shows none.
  useEffect(() => {
    const company = (customer?.company_name || '').trim()
    if (!company) { setCompanyContacts([]); return }
    let active = true
    apiGet<any>('/contacts', { search: company, pageSize: 200 })
      .then((res) => {
        if (!active) return
        const list = Array.isArray(res) ? res : res.contacts || res.data || []
        const norm = company.toLowerCase()
        const alias = (customer?.alias || '').trim().toLowerCase()
        setCompanyContacts(
          list.filter((c: any) => {
            const cc = String(c.company || '').trim().toLowerCase()
            return cc && (cc === norm || (!!alias && cc === alias) || cc.includes(norm) || norm.includes(cc))
          })
        )
      })
      .catch(() => { if (active) setCompanyContacts([]) })
    return () => { active = false }
  }, [customer?.company_name, customer?.alias])

  const handleUnlink = async (linkId: number) => {
    if (!confirm('Unlink this channel?')) return
    try {
      await apiDelete(`/paid-customers/${id}/link/${linkId}`)
      fetchData()
    } catch (err: any) { alert(err.message) }
  }

  const getLinksForType = (type: string) => links.filter(l => l.channel_type === type && l.link_status === 'linked')

  const zohoLinks = useMemo(() => getLinksForType('zoho'), [links])
  const zohoFiltered = useMemo(() => {
    if (!zohoSearch.trim()) return zohoLinks
    const q = zohoSearch.trim().toLowerCase()
    return zohoLinks.filter(l =>
      l.channel_ref_id.toLowerCase().includes(q) ||
      (l.channel_name || '').toLowerCase().includes(q)
    )
  }, [zohoLinks, zohoSearch])

  // Parse users from extra_data JSON (must be before ALL early returns to maintain hook order)
  const companyUsers: CompanyUser[] = useMemo(() => {
    if (!customer?.extra_data) return []
    try {
      const parsed = typeof customer.extra_data === 'string' ? JSON.parse(customer.extra_data) : customer.extra_data
      return (parsed.users || []).map((u: any) => ({
        name: u.name || '',
        email: u.email || '',
        phone: u.phone || '',
      }))
    } catch { return [] }
  }, [customer?.extra_data])

  const handleChannelClick = (link: ChannelLink) => {
    if (link.channel_type === 'wa_archive') navigate(`/whatsapp-archive`)
    else if (link.channel_type === 'wa_live') {
      // Deep-link to the specific group using the group name as the slug
      const slug = encodeURIComponent(link.channel_name || '')
      navigate(slug ? `/groups/${slug}` : `/groups`)
    }
    else if (link.channel_type === 'zoho') navigate(`/zoho-chats`)
    else if (link.channel_type === 'widget') navigate(`/history`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-txt-secondary">Loading company details...</div>
      </div>
    )
  }

  if (!customer) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-txt-secondary">Customer not found</p>
          <button onClick={() => navigate('/customers')} className="mt-2 text-sm text-brand-600 hover:underline">Back to Customers</button>
        </div>
      </div>
    )
  }

  const fullAddress = [customer.address_line_1, customer.address_line_2, customer.address_line_3, customer.city, customer.county, customer.postcode, customer.country]
    .filter(Boolean).join(', ')


  // Determine channel rows for the communication links table
  const channelRows = [
    { type: 'wa_live', label: 'WhatsApp Group', statusLabel: 'LIVE' },
    { type: 'wa_archive', label: 'WhatsApp Group', statusLabel: 'ARCHIVED' },
    { type: 'widget', label: 'Live Website Support Widget Chat', statusLabel: 'ARCHIVED' },
    { type: 'zoho', label: 'Zoho Chat', statusLabel: 'ARCHIVED' },
  ]

  return (
    <div className="h-full flex flex-col overflow-hidden bg-surface-secondary">
      {/* Header (static) */}
      <div className="flex-shrink-0 bg-surface border-b border-border px-6 py-4">
        <div className="flex items-center gap-2 text-sm text-txt-secondary mb-2">
          <button onClick={() => navigate('/customers')} className="hover:text-brand-600">Company</button>
          <span>&gt;</span>
          <span className="text-txt-primary font-medium">{customer.company_name}</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-txt-primary">Company Details</h1>
            <p className="text-sm text-txt-secondary">Real-time visitor tracking and engagement</p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowEdit(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-txt-primary bg-surface border border-border rounded-lg hover:bg-surface-hover">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
              Edit Details
            </button>
          </div>
        </div>
      </div>

      {/* Content: two-column body.
          On md+ each column scrolls independently (left static/full-height,
          right scrollable). Below md it stacks and the whole body scrolls. */}
      <div className="flex-1 min-h-0 flex flex-col md:flex-row gap-6 p-6 overflow-y-auto md:overflow-hidden">
        {/* Left: Company Info — fixed width, full viewport height, static.
            Its own overflow-y-auto only kicks in if the details exceed the
            screen; otherwise it simply stays put while the right column scrolls. */}
        <div className="w-full md:w-80 lg:w-96 md:flex-shrink-0 md:h-full md:overflow-y-auto">
          <div className="bg-surface rounded-xl border border-border p-6">
              <div className="mb-6">
                {customer.company_logo_url ? (
                  <div className="mb-4 bg-surface-secondary rounded-xl p-4 flex items-center justify-center">
                    <img src={customer.company_logo_url} alt={customer.company_name}
                      className="max-w-full max-h-24 object-contain" />
                  </div>
                ) : (
                  <div className="mb-4 w-16 h-16 rounded-xl bg-brand-100 text-brand-700 flex items-center justify-center text-xl font-bold">
                    {customer.company_name.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                )}
                <h2 className="text-lg font-bold text-txt-primary">{customer.company_name}</h2>
                {fullAddress && <p className="text-xs text-txt-secondary mt-1 leading-relaxed">{fullAddress}</p>}
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-txt-secondary font-medium">ID</span><span className="text-txt-primary font-semibold">{customer.wequote_id || '—'}</span></div>
                <div className="flex justify-between"><span className="text-txt-secondary font-medium">Alias</span><span className="text-txt-primary font-semibold text-xs">{customer.alias || '—'}</span></div>
                <div className="flex justify-between"><span className="text-txt-secondary font-medium">Account Type</span><span className="text-txt-primary font-semibold">{customer.account_type || '—'}</span></div>
                <div className="flex justify-between"><span className="text-txt-secondary font-medium">Stock Module</span><span className="text-txt-primary font-semibold">{customer.stock_module_version || '—'}</span></div>
                {customer.contact_name && <div className="flex justify-between"><span className="text-txt-secondary font-medium">Contact</span><span className="text-txt-primary">{customer.contact_name}</span></div>}
                {customer.email && <div className="flex justify-between"><span className="text-txt-secondary font-medium">Email</span><span className="text-txt-primary text-xs">{customer.email}</span></div>}
                {customer.phone && <div className="flex justify-between"><span className="text-txt-secondary font-medium">Phone</span><span className="text-txt-primary">{customer.phone}</span></div>}
              </div>

              {/* Quick Stats */}
              <div className="mt-6 pt-6 border-t border-border">
                <h3 className="text-xs font-semibold text-txt-secondary uppercase tracking-wider mb-3">Quick Stats</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-surface-secondary rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-txt-primary">{activity?.totalMessages || 0}</p>
                    <p className="text-xs text-txt-secondary">Total Messages</p>
                  </div>
                  <div className="bg-surface-secondary rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-txt-primary">{customer.widget_visit_count || 0}</p>
                    <p className="text-xs text-txt-secondary">Widget Visits</p>
                  </div>
                  <div className="bg-surface-secondary rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-txt-primary">{engagementScore !== null ? Math.round(engagementScore) : '—'}</p>
                    <p className="text-xs text-txt-secondary">Engagement</p>
                  </div>
                  <div className="bg-surface-secondary rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-txt-primary">{activeChannelsCount}</p>
                    <p className="text-xs text-txt-secondary">Active Channels</p>
                  </div>
                </div>
                {/* Activity Sparkline */}
                <ActivitySparkline activity={activity} />
              </div>
            </div>

          </div>

          {/* Right: Communication Links + Activity — independently scrollable,
              organised into collapsible accordion sections. */}
          <div className="flex-1 min-w-0 md:h-full md:overflow-y-auto flex flex-col gap-4 pr-0.5">
            {/* Channel Activity Summary */}
            {activity && (
              <AccordionSection
                title="Channel Activity Summary"
                open={openSections.channelSummary}
                onToggle={() => toggleSection('channelSummary')}
                bodyClassName="border-t border-border p-6"
              >
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: 'WA Archive', data: activity.wa_archive, colour: 'green' },
                    { label: 'WA Live', data: activity.wa_live, colour: 'emerald' },
                    { label: 'Zoho Chat', data: activity.zoho, colour: 'orange' },
                    { label: 'Widget', data: activity.widget, colour: 'blue' },
                  ].map(ch => (
                    <div key={ch.label} className="bg-surface-secondary rounded-lg p-4">
                      <p className="text-xs font-medium text-txt-secondary mb-1">{ch.label}</p>
                      <p className="text-xl font-bold text-txt-primary">{ch.data?.messageCount || 0}</p>
                      <p className="text-xs text-txt-secondary">messages</p>
                    </div>
                  ))}
                </div>
              </AccordionSection>
            )}

            {/* Communication Links */}
            <AccordionSection
              title="Communication Links"
              open={openSections.links}
              onToggle={() => toggleSection('links')}
              bodyClassName="border-t border-border"
              icon={<svg className="w-4 h-4 text-brand-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>}
            >
              <table className="w-full">
                <thead>
                  <tr className="bg-surface-secondary text-xs font-semibold text-txt-secondary uppercase">
                    <th className="text-left px-6 py-3">Platform</th>
                    <th className="text-left px-4 py-3">Instance Name</th>
                    <th className="text-center px-4 py-3">Status</th>
                    <th className="text-right px-6 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {channelRows.map(ch => {
                    const linked = getLinksForType(ch.type)

                    {/* ── Zoho: collapsible card ── */}
                    if (ch.type === 'zoho') {
                      if (linked.length > 0) {
                        return (
                          <React.Fragment key="zoho-group">
                            {/* Summary row — click to expand card */}
                            <tr className="border-t border-border hover:bg-surface-hover cursor-pointer transition-colors duration-100"
                              onClick={() => setZohoExpanded(prev => !prev)}>
                              <td className="px-6 py-3">
                                <div className="flex items-center gap-3">
                                  <ChannelIcon type="zoho" />
                                  <span className="text-sm font-medium text-txt-primary">Zoho Chat</span>
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-700">
                                    {linked.length} {linked.length === 1 ? 'chat' : 'chats'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-txt-secondary">{linked[0]?.channel_name || '—'}</td>
                              <td className="px-4 py-3 text-center">
                                <span className="inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">ARCHIVED</span>
                              </td>
                              <td className="px-6 py-3 text-right">
                                <span className="inline-flex items-center gap-1 text-xs text-txt-secondary">
                                  {zohoExpanded ? 'Collapse' : 'Expand'}
                                  <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${zohoExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                                </span>
                              </td>
                            </tr>

                            {/* Expanded card rendered as standalone section below */}
                          </React.Fragment>
                        )
                      }
                      // Zoho not connected
                      return (
                        <tr key={ch.type} className="border-t border-border">
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-3">
                              <ChannelIcon type={ch.type} />
                              <span className="text-sm font-medium text-txt-secondary">{ch.label}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-txt-secondary italic">Not Connected</td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-surface-secondary text-txt-secondary">{ch.statusLabel}</span>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <button onClick={() => setLinkingChannel(ch.type)}
                              className="px-3 py-1 text-xs font-medium text-brand-600 border border-brand-200 rounded-md hover:bg-brand-50">Link</button>
                          </td>
                        </tr>
                      )
                    }

                    {/* ── Non-Zoho channels: render as before ── */}
                    if (linked.length > 0) {
                      return linked.map(link => (
                        <tr key={link.id} className="border-t border-border hover:bg-surface-hover cursor-pointer transition-colors duration-100" onClick={() => handleChannelClick(link)}>
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-3">
                              <ChannelIcon type={ch.type} />
                              <span className="text-sm font-medium text-txt-primary">{ch.label}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {link.channel_name ? (
                              <span className="text-brand-600 hover:text-brand-700 hover:underline font-medium cursor-pointer">{link.channel_name}</span>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                              ch.statusLabel === 'LIVE' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }`}>{ch.statusLabel}</span>
                          </td>
                          <td className="px-6 py-3 text-right" onClick={e => e.stopPropagation()}>
                            <button onClick={() => handleUnlink(link.id)}
                              className="px-3 py-1 text-xs font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50">Unlink</button>
                          </td>
                        </tr>
                      ))
                    }
                    return (
                      <tr key={ch.type} className="border-t border-border">
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-3">
                            <ChannelIcon type={ch.type} />
                            <span className="text-sm font-medium text-txt-secondary">{ch.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-txt-secondary italic">Not Connected</td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-surface-secondary text-txt-secondary">{ch.statusLabel}</span>
                        </td>
                        <td className="px-6 py-3 text-right">
                          <button onClick={() => setLinkingChannel(ch.type)}
                            className="px-3 py-1 text-xs font-medium text-brand-600 border border-brand-200 rounded-md hover:bg-brand-50">Link</button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </AccordionSection>

            {/* Zoho Archived Chats — standalone card (nested under Communication Links) */}
            {zohoExpanded && zohoLinks.length > 0 && (
              <div className="bg-surface rounded-xl border border-border">
                <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                  <h3 className="text-sm font-semibold text-txt-primary flex items-center gap-2">
                    <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                    Zoho Archived Chats
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-700">
                      {zohoLinks.length}
                    </span>
                  </h3>
                  <button onClick={() => setZohoExpanded(false)}
                    className="text-xs text-txt-secondary hover:text-txt-primary flex items-center gap-1">
                    Collapse
                    <svg className="w-3.5 h-3.5 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                  </button>
                </div>
                {/* Search */}
                <div className="px-6 py-3 border-b border-border">
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-secondary pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search by chat # or name..."
                      value={zohoSearch}
                      onChange={e => setZohoSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 text-sm rounded-lg border border-border bg-surface text-txt-primary placeholder:text-txt-secondary focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-txt-secondary mt-2">Showing {zohoFiltered.length} of {zohoLinks.length} chats</p>
                </div>
                {/* Scrollable list */}
                <div className="max-h-[420px] overflow-y-auto divide-y divide-border">
                  {zohoFiltered.length === 0 ? (
                    <div className="px-6 py-8 text-center">
                      <p className="text-sm text-txt-secondary">No chats match your search</p>
                    </div>
                  ) : zohoFiltered.map(link => (
                    <div
                      key={link.id}
                      className="flex items-center gap-3 px-6 py-3 hover:bg-surface-hover cursor-pointer transition-colors duration-100 group"
                      onClick={() => setZohoModalChatId(Number(link.channel_ref_id))}
                    >
                      <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-txt-primary">Chat #{link.channel_ref_id}</p>
                        {link.channel_name && (
                          <p className="text-xs text-txt-secondary truncate">{link.channel_name}</p>
                        )}
                      </div>
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">ARCHIVED</span>
                      <div className="flex items-center gap-2 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-xs text-brand-600 font-medium">View</span>
                        <button
                          onClick={e => { e.stopPropagation(); handleUnlink(link.id) }}
                          className="px-2 py-0.5 text-[10px] font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50"
                        >Unlink</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Communications Feed */}
            <AccordionSection
              title="Recent Communications"
              open={openSections.recent}
              onToggle={() => toggleSection('recent')}
              bodyClassName="border-t border-border"
              icon={<svg className="w-4 h-4 text-brand-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>}
              badge={<span className="text-xs font-medium text-txt-secondary bg-surface-secondary rounded-full px-2 py-0.5 flex-shrink-0">{recentMessages.length}</span>}
            >
              <div className="divide-y max-h-[400px] overflow-y-auto">
                {recentMessages.length === 0 ? (
                  <div className="px-6 py-8 text-center">
                    <p className="text-sm text-txt-secondary">No communications found</p>
                  </div>
                ) : (
                  recentMessages.map(msg => (
                    <div key={msg.id} className="px-6 py-3 hover:bg-surface-hover transition-colors duration-100">
                      <div className="flex items-start gap-3">
                        {msg.channel === 'wa_live' || msg.channel === 'wa_archive' ? (
                          <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /></svg>
                          </div>
                        ) : msg.channel === 'zoho' ? (
                          <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg className="w-4 h-4 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" /></svg>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2 mb-1">
                            <p className="text-sm font-medium text-txt-primary">{msg.sender_name || 'Unknown'}</p>
                            <span className="text-xs text-txt-secondary flex-shrink-0">{formatMessageTime(msg.created_at)}</span>
                          </div>
                          <p className="text-sm text-txt-secondary line-clamp-2">{truncateMessage(msg.message_text)}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </AccordionSection>

            {/* Support Chats — all this company's chats + people on them */}
            <AccordionSection
              title="Support Chats"
              open={openSections.chats}
              onToggle={() => toggleSection('chats')}
              bodyClassName=""
              badge={<span className="text-xs px-2 py-0.5 rounded-full bg-surface-secondary text-txt-secondary flex-shrink-0">{companyChats.length}</span>}
            >
              <div className="px-5 py-3 border-t border-border text-right">
                <span className="text-xs text-txt-secondary">Total support time: <span className="font-semibold text-txt-primary">{fmtSecs(chatTotalSecs)}</span></span>
              </div>

              {chatPeople.length > 0 && (
                <div className="px-5 py-3 border-b border-border">
                  <p className="text-[11px] font-semibold text-txt-secondary uppercase tracking-wider mb-2">People on these chats</p>
                  <div className="flex flex-wrap gap-2">
                    {chatPeople.map((p, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-surface-secondary text-xs" title={p.email}>
                        <span className="font-medium text-txt-primary">{p.name || p.email}</span>
                        <span className="text-txt-tertiary">· {p.chats} chat{p.chats !== 1 ? 's' : ''} · {fmtSecs(p.support_seconds)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="divide-y divide-border max-h-96 overflow-y-auto">
                {companyChats.length > 0 ? companyChats.map(c => (
                  <button
                    key={c.id}
                    onClick={() => window.open(`/queue/${c.id}`, '_blank', 'noopener')}
                    className="w-full flex items-center justify-between gap-3 px-5 py-3 hover:bg-surface-secondary transition-colors text-left group"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-txt-primary truncate">
                        Chat #{c.id}{c.visitor_name ? ` · ${c.visitor_name}` : (c.visitor_email ? ` · ${c.visitor_email}` : '')}
                      </p>
                      <p className="text-xs text-txt-tertiary">
                        {new Date(c.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} · {fmtSecs(c.support_seconds)}
                      </p>
                    </div>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] font-medium px-1.5 py-px rounded capitalize ${isChatResolved(c) ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{isChatResolved(c) ? 'Resolved' : (chatResolution(c) || c.status || '—')}</span>
                      <svg className="w-4 h-4 text-txt-tertiary group-hover:text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                    </span>
                  </button>
                )) : (
                  <p className="px-5 py-6 text-sm text-txt-secondary text-center">No support chats found for this company yet.</p>
                )}
              </div>
            </AccordionSection>

            {/* Users Table */}
            <AccordionSection
              title="Users"
              open={openSections.users}
              onToggle={() => toggleSection('users')}
              bodyClassName="border-t border-border"
              icon={(
                <span className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                  </svg>
                </span>
              )}
              badge={(
                <span className="text-xs font-medium text-txt-secondary bg-surface-secondary rounded-full px-2 py-0.5 flex-shrink-0">
                  {companyUsers.length || customer.user_count || 0}
                </span>
              )}
            >
              <div>
                {companyUsers.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-surface-secondary">
                            <th className="text-left px-4 py-2 font-semibold text-txt-secondary">Name</th>
                            <th className="text-left px-4 py-2 font-semibold text-txt-secondary">Email</th>
                            <th className="text-left px-4 py-2 font-semibold text-txt-secondary">Phone</th>
                          </tr>
                        </thead>
                        <tbody>
                          {companyUsers.map((u, i) => (
                            <tr key={i} className="border-t border-border hover:bg-surface-secondary/50 transition-colors">
                              <td className="px-4 py-2.5 text-txt-primary font-medium whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                    {u.name ? u.name.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?'}
                                  </div>
                                  {u.name || '—'}
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-txt-secondary">
                                {u.email ? (
                                  <a href={`mailto:${u.email}`} className="text-brand-600 hover:underline">{u.email}</a>
                                ) : '—'}
                              </td>
                              <td className="px-4 py-2.5 text-txt-secondary whitespace-nowrap">{u.phone || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="px-5 py-6 text-center text-xs text-txt-secondary">
                      No user details available. Import the customer CSV to populate this data.
                    </div>
                  )}
              </div>
            </AccordionSection>

            {/* Contacts (from the Contacts database) associated to this company. */}
            <AccordionSection
              title="Contacts"
              open={openSections.contacts}
              onToggle={() => toggleSection('contacts')}
              bodyClassName="border-t border-border"
              icon={(
                <span className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
                  </svg>
                </span>
              )}
              badge={(
                <span className="text-xs font-medium text-txt-secondary bg-surface-secondary rounded-full px-2 py-0.5 flex-shrink-0">
                  {companyContacts.length}
                </span>
              )}
            >
              <div>
                {companyContacts.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-surface-secondary">
                          <th className="text-left px-4 py-2 font-semibold text-txt-secondary">Name</th>
                          <th className="text-left px-4 py-2 font-semibold text-txt-secondary">Email</th>
                          <th className="text-left px-4 py-2 font-semibold text-txt-secondary">Phone</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyContacts.map((c, i) => {
                          const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || '—'
                          return (
                            <tr key={c.id || i} className="border-t border-border hover:bg-surface-secondary/50 transition-colors">
                              <td className="px-4 py-2.5 text-txt-primary font-medium whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                    {name !== '—' ? name.split(/\s+/).map((w: string) => w[0]).join('').slice(0, 2).toUpperCase() : '?'}
                                  </div>
                                  {name}
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-txt-secondary">
                                {c.email ? <a href={`mailto:${c.email}`} className="text-brand-600 hover:underline">{c.email}</a> : '—'}
                              </td>
                              <td className="px-4 py-2.5 text-txt-secondary whitespace-nowrap">{c.phone || '—'}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-5 py-6 text-center text-xs text-txt-secondary">
                    No contacts linked to this company yet.
                  </div>
                )}
                <div className="px-4 py-3 border-t border-border">
                  <button
                    onClick={() => navigate(`/contacts?search=${encodeURIComponent(customer.company_name || '')}`)}
                    className="text-xs font-medium text-brand-600 hover:underline"
                  >
                    Manage in Contacts →
                  </button>
                </div>
              </div>
            </AccordionSection>

          </div>
        </div>

      {/* Modals */}
      {showEdit && customer && <EditModal customer={customer} onClose={() => setShowEdit(false)} onSaved={fetchData} />}
      {linkingChannel && customer && (
        <LinkSearchModal channelType={linkingChannel} customerId={customer.id}
          onClose={() => setLinkingChannel(null)} onLinked={fetchData} />
      )}
      {zohoModalChatId && <ZohoChatModal chatId={zohoModalChatId} onClose={() => setZohoModalChatId(null)} />}
    </div>
  )
}
